

import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
  useGetEnglishQuestionGroupByIdQuery,
  useGetQuestionsByEnglishQuestionGroupQuery,
  useToggleEnglishQuestionGroupStatusMutation,
  useRecalculateEnglishQuestionGroupMutation,
  useDeleteEnglishQuestionGroupMutation,
} from "../../slices/englishQuestionGroupApiSlice";

import GroupStatusBadge from "./components/GroupStatusBadge";
import EnglishQuestionGroupQuestionList from "./components/EnglishQuestionGroupQuestionList";

const EnglishQuestionGroupDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // ======================================================
  // Group
  // ======================================================

  const {
    data: group,
    isLoading: isGroupLoading,
    isError: isGroupError,
    error: groupError,
    refetch: refetchGroup,
  } = useGetEnglishQuestionGroupByIdQuery(id, {
    skip: !id,
  });

  // ======================================================
  // Questions
  // ======================================================

  const {
    data: questionsResponse,
    isLoading: isQuestionsLoading,
    isError: isQuestionsError,
    error: questionsError,
    refetch: refetchQuestions,
  } = useGetQuestionsByEnglishQuestionGroupQuery(id, {
    skip: !id,
  });

  // ======================================================
  // Mutations
  // ======================================================

  const [
    toggleStatus,
    { isLoading: isTogglingStatus },
  ] = useToggleEnglishQuestionGroupStatusMutation();

  const [
    recalculateGroup,
    { isLoading: isRecalculating },
  ] = useRecalculateEnglishQuestionGroupMutation();

  const [
    deleteGroup,
    { isLoading: isDeleting },
  ] = useDeleteEnglishQuestionGroupMutation();

  // ======================================================
  // Helpers
  // ======================================================

  const questions =
    questionsResponse?.questions ||
    questionsResponse?.data ||
    (Array.isArray(questionsResponse)
      ? questionsResponse
      : []);

  const questionCount =
    group?.questionCount ??
    group?.totalQuestions ??
    questions.length;

  const formatGroupType = (value) => {
    if (!value) return "—";

    return String(value)
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (char) => char.toUpperCase());
  };

  const formatDate = (value) => {
    if (!value) return "—";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "—";
    }

    return date.toLocaleString();
  };

  // ======================================================
  // Actions
  // ======================================================

  const handleToggleStatus = async () => {
    try {
      await toggleStatus(id).unwrap();

      toast.success(
        group?.isActive
          ? "Question group deactivated successfully"
          : "Question group activated successfully",
      );

      refetchGroup();
    } catch (err) {
      toast.error(
        err?.data?.message ||
          err?.error ||
          "Failed to update question group status",
      );
    }
  };

  const handleRecalculate = async () => {
    try {
      await recalculateGroup(id).unwrap();

      toast.success(
        "Question group statistics recalculated successfully",
      );

      refetchGroup();
      refetchQuestions();
    } catch (err) {
      toast.error(
        err?.data?.message ||
          err?.error ||
          "Failed to recalculate question group",
      );
    }
  };

  const handleDelete = async () => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this English question group? This action cannot be undone.",
    );

    if (!confirmed) return;

    try {
      await deleteGroup(id).unwrap();

      toast.success(
        "English question group deleted successfully",
      );

      navigate("/english-questions-groups");
    } catch (err) {
      toast.error(
        err?.data?.message ||
          err?.error ||
          "Failed to delete English question group",
      );
    }
  };

  // ======================================================
  // Loading
  // ======================================================

  if (isGroupLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 w-72 rounded bg-slate-200" />
            <div className="h-4 w-96 max-w-full rounded bg-slate-200" />

            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                <div className="h-20 rounded bg-slate-200" />
                <div className="h-20 rounded bg-slate-200" />
                <div className="h-20 rounded bg-slate-200" />
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="h-6 w-48 rounded bg-slate-200" />
              <div className="mt-6 space-y-4">
                <div className="h-20 rounded bg-slate-200" />
                <div className="h-20 rounded bg-slate-200" />
                <div className="h-20 rounded bg-slate-200" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ======================================================
  // Error
  // ======================================================

  if (isGroupError || !group) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="rounded-2xl border border-red-200 bg-white p-8 text-center shadow-sm">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-100 text-xl font-bold text-red-600">
              !
            </div>

            <h1 className="mt-5 text-xl font-semibold text-slate-900">
              Unable to load question group
            </h1>

            <p className="mt-2 text-sm text-slate-500">
              {groupError?.data?.message ||
                groupError?.error ||
                "The requested English question group could not be found."}
            </p>

            <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => refetchGroup()}
                className="rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-indigo-700"
              >
                Try Again
              </button>

              <button
                type="button"
                onClick={() =>
                  navigate("/english-questions-groups")
                }
                className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
              >
                Back to Groups
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ======================================================
  // Main
  // ======================================================

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {/* ==================================================
            Header
        ================================================== */}

        <div className="mb-6">
          <div className="mb-2 flex items-center gap-2 text-sm text-slate-500">
            <button
              type="button"
              onClick={() =>
                navigate("/english-questions-groups")
              }
              className="transition hover:text-indigo-600"
            >
              English Question Groups
            </button>

            <span>/</span>

            <span className="text-slate-700">
              Details
            </span>
          </div>

          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                  {group.name || "Untitled Question Group"}
                </h1>

                <GroupStatusBadge
                  isActive={group.isActive}
                  status={group.status}
                />
              </div>

              <p className="mt-2 text-sm text-slate-500">
                {formatGroupType(group.groupType)}
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() =>
                  navigate(
                    `/english-questions-groups/${id}/edit`,
                  )
                }
                disabled={isDeleting}
                className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Edit
              </button>

              <button
                type="button"
                onClick={handleToggleStatus}
                disabled={isTogglingStatus}
                className={`inline-flex items-center justify-center rounded-lg px-4 py-2.5 text-sm font-medium shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60 ${
                  group.isActive
                    ? "bg-amber-500 text-white hover:bg-amber-600"
                    : "bg-emerald-600 text-white hover:bg-emerald-700"
                }`}
              >
                {isTogglingStatus
                  ? "Updating..."
                  : group.isActive
                    ? "Deactivate"
                    : "Activate"}
              </button>

              <button
                type="button"
                onClick={handleDelete}
                disabled={isDeleting}
                className="inline-flex items-center justify-center rounded-lg bg-red-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>

        {/* ==================================================
            Overview
        ================================================== */}

        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
              Group Type
            </p>

            <p className="mt-2 text-lg font-semibold text-slate-900">
              {formatGroupType(group.groupType)}
            </p>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
              Questions
            </p>

            <p className="mt-2 text-lg font-semibold text-slate-900">
              {questionCount}
            </p>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
              Created
            </p>

            <p className="mt-2 text-sm font-semibold text-slate-900">
              {formatDate(group.createdAt)}
            </p>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
              Updated
            </p>

            <p className="mt-2 text-sm font-semibold text-slate-900">
              {formatDate(group.updatedAt)}
            </p>
          </div>
        </div>

        {/* ==================================================
            Group Content
        ================================================== */}

        <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Directions */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-slate-900">
                Directions
              </h2>
            </div>

            <div className="p-6">
              {group.directions ? (
                <p className="whitespace-pre-wrap text-sm leading-6 text-slate-600">
                  {group.directions}
                </p>
              ) : (
                <p className="text-sm italic text-slate-400">
                  No directions provided.
                </p>
              )}
            </div>
          </div>

          {/* Shared Content */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-slate-900">
                Shared Content
              </h2>
            </div>

            <div className="p-6">
              {group.sharedContent?.text ? (
                <p className="whitespace-pre-wrap text-sm leading-6 text-slate-600">
                  {group.sharedContent.text}
                </p>
              ) : group.sharedContent?.imageUrl ? (
                <img
                  src={group.sharedContent.imageUrl}
                  alt="Group shared content"
                  className="max-h-80 w-full rounded-xl border border-slate-200 object-contain"
                />
              ) : (
                <p className="text-sm italic text-slate-400">
                  No shared content provided.
                </p>
              )}

              {group.sharedContent?.type && (
                <div className="mt-4">
                  <span className="inline-flex rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium capitalize text-indigo-700">
                    {group.sharedContent.type}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ==================================================
            Additional Information
        ================================================== */}

        <div className="mb-6 rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-6 py-4">
            <h2 className="text-lg font-semibold text-slate-900">
              Group Information
            </h2>
          </div>

          <div className="grid grid-cols-1 divide-y divide-slate-100 md:grid-cols-2 md:divide-x md:divide-y-0">
            <div className="space-y-4 p-6">
              <InfoRow
                label="Subject"
                value={
                  group.subject?.name ||
                  group.subjectName ||
                  "—"
                }
              />

              <InfoRow
                label="Chapter"
                value={
                  group.chapter?.name ||
                  group.chapterName ||
                  "—"
                }
              />

              <InfoRow
                label="Group Type"
                value={formatGroupType(group.groupType)}
              />
            </div>

            <div className="space-y-4 p-6">
              <InfoRow
                label="Question Count"
                value={questionCount}
              />

              <InfoRow
                label="Status"
                value={
                  group.isActive
                    ? "Active"
                    : "Inactive"
                }
              />

              <InfoRow
                label="Created By"
                value={
                  group.createdBy?.name ||
                  group.createdBy?.email ||
                  "—"
                }
              />
            </div>
          </div>
        </div>

        {/* ==================================================
            Questions
        ================================================== */}

        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-col gap-4 border-b border-slate-200 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                Questions
              </h2>

              <p className="mt-1 text-sm text-slate-500">
                Questions belonging to this English question group.
              </p>
            </div>

            <button
              type="button"
              onClick={handleRecalculate}
              disabled={
                isRecalculating ||
                isQuestionsLoading
              }
              className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isRecalculating
                ? "Recalculating..."
                : "Recalculate"}
            </button>
          </div>

          <div className="p-6">
            {isQuestionsLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((item) => (
                  <div
                    key={item}
                    className="animate-pulse rounded-xl border border-slate-200 p-5"
                  >
                    <div className="h-5 w-2/3 rounded bg-slate-200" />
                    <div className="mt-3 h-4 w-full rounded bg-slate-200" />
                    <div className="mt-2 h-4 w-5/6 rounded bg-slate-200" />
                  </div>
                ))}
              </div>
            ) : isQuestionsError ? (
              <div className="rounded-xl border border-red-200 bg-red-50 p-5">
                <p className="text-sm font-medium text-red-800">
                  Unable to load questions.
                </p>

                <p className="mt-1 text-sm text-red-700">
                  {questionsError?.data?.message ||
                    questionsError?.error ||
                    "Something went wrong while loading the questions."}
                </p>

                <button
                  type="button"
                  onClick={() => refetchQuestions()}
                  className="mt-4 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
                >
                  Try Again
                </button>
              </div>
            ) : (
              <EnglishQuestionGroupQuestionList
                questions={questions}
                group={group}
                isLoading={isQuestionsLoading}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ======================================================
// Info Row
// ======================================================

const InfoRow = ({ label, value }) => {
  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
      <span className="text-sm text-slate-500">
        {label}
      </span>

      <span className="text-sm font-medium text-slate-900 sm:text-right">
        {value}
      </span>
    </div>
  );
};

export default EnglishQuestionGroupDetails;

